import React, { FormEvent, useEffect, useState } from 'react';
import { useQuery } from '@apollo/react-hooks';
import { useAuth0 } from '@auth0/auth0-react';
import _ from 'lodash';
import { Stack, TextField, Toggle } from '@fluentui/react';
import {
  default_page_size,
  stackTokens15,
  textFieldStyles300,
} from '../../utils';
import { GET_CARS, ICar } from '../../utils/Car';
import useDebounce from '../../components/hooks/useDebounce';
import { useAppDispatch } from '../../redux/hooks';
import { SeverityLevel, throwError } from '../../redux/error/errorSlice';
import CarsOverview from './components/CarsOverview';

interface IPagedCar {
  cars: ICar[];
  skip: number;
  take: number;
  filter: string;
  initialLoad: boolean;
  showLoadMore: boolean;
  isFiltered: boolean;
  isSortedAsc: boolean;
  sortedField: string;
}

const Cars = () => {
  const { isAuthenticated } = useAuth0();
  const dispatch = useAppDispatch();
  const [pagedState, setPagedState] = useState<IPagedCar>({
    cars: [],
    skip: 0,
    take: default_page_size,
    filter: '',
    showLoadMore: true,
    isFiltered: false,
    initialLoad: true,
    isSortedAsc: true,
    sortedField: 'name',
  });

  const [filter, setFilter] = useState('');
  const [inUse, setInUse] = useState(true);

  useEffect(() => {
    setPagedState(prevState => ({ ...prevState, skip: 0, cars: [] }));
  }, [filter, inUse]);

  const debouncedValue = useDebounce(filter, 500);

  const getOrderBy = () => {
    const orderByObject: any = {}; // deliberately kept any as type to index by string
    orderByObject[pagedState.sortedField] = pagedState.isSortedAsc
      ? 'asc'
      : 'desc';
    return orderByObject;
  };

  const getFilters = (filterString: string, active: boolean) => {
    const filterArray = filterString.split(' ');

    const filterObject: any = {
      AND: [{ active }],
    };
    for (let i = 0; i < filterArray.length; i++) {
      const filterValue: any = {
        OR: [
          { name: { contains: filterArray[i] } },
          { car_type: { contains: filterArray[i] } },
          { license_plate: { contains: filterArray[i] } },
          {
            car_driver: {
              some: {
                employee: {
                  OR: [
                    { first_name: { contains: filterArray[i] } },
                    { last_name: { contains: filterArray[i] } },
                  ],
                },
              },
            },
          },
        ],
      };

      filterObject.AND.push(filterValue);
    }

    return filterObject;
  };

  const variables = {
    filter: getFilters(debouncedValue, inUse), // inUse == active?
    take: pagedState.take,
    skip: 0,
    orderBy: getOrderBy(),
  };

  const {
    data: dataCars,
    loading,
    error,
    fetchMore,
  } = useQuery(GET_CARS, {
    // notifyOnNetworkStatusChange: true,
    variables: {
      filter: getFilters(debouncedValue, inUse),
      take: pagedState.take,
      skip: 0,
      orderBy: getOrderBy(),
    },
    onError: error => {
      dispatch(
        throwError({
          module: 'car Overview',
          message: error.message,
          level: SeverityLevel.Critical,
        }),
      );
    },
  });

  const onSearchValueChange = (
    event: FormEvent<HTMLInputElement | HTMLTextAreaElement>,
    newValue?: string | undefined,
  ) => {
    const filterString = newValue ? newValue.toLowerCase() : '';
    setFilter(filterString);
  };

  const onChangeFilterInUse = () => {
    setInUse(!inUse);
    clearCars();
    // refetchCars();
  };

  const setSorting = (isSortedAsc: boolean, sortedField: string) => {
    clearCars();
    setPagedState(prevState => ({
      ...prevState,
      isSortedAsc,
      sortedField,
    }));
  };

  const clearCars = () => {
    setPagedState(prevState => ({
      ...prevState,
      cars: [],
      skip: 0,
    }));
  };

  const debouncedScroll = _.debounce(() => {
    setPagedState(prevState => {
      const currentDataLength = prevState.cars.length;
      return {
        ...prevState,
        skip: currentDataLength,
      };
    });
  }, 250);

  const fetchMoreCars = () => {
    fetchMore({
      variables: {
        filter: getFilters(debouncedValue, inUse), // inUse == active?
        take: pagedState.take,
        skip: pagedState.skip + pagedState.take,
        orderBy: getOrderBy(),
      },
    });

    setPagedState(prevState => ({
      ...prevState,
      skip: pagedState.skip + pagedState.take,
    }));
  };

  if (!isAuthenticated) return <p>Verboden</p>;
  if (error) return <p>Oeps, er ging iets mis...</p>;

  return (
    <Stack tokens={stackTokens15}>
      <h3 style={{ marginBottom: 0 }}>Wagenpark</h3>

      <Stack style={{ flexDirection: 'row' }}>
        <TextField
          label='Zoeken...'
          value={filter}
          styles={textFieldStyles300}
          onChange={onSearchValueChange}
        />

        <Stack style={{ marginLeft: 70 }}>
          <Toggle
            label='In gebruik?'
            defaultChecked={inUse}
            onText='In gebruik'
            offText='Niet in gebruik'
            onChange={onChangeFilterInUse}
          />
        </Stack>
      </Stack>

      <CarsOverview
        cars={
          dataCars
            ? dataCars.findManyCars
            : []
        }
        // setCars={setPagedState}
        loading={loading}
        // initialLoad={pagedState.initialLoad}
        setSorting={setSorting}
        isSortedAsc={pagedState.isSortedAsc}
        sortedField={pagedState.sortedField}
        showLoadMore={pagedState.showLoadMore}
        loadMore
        loadMoreCallback={fetchMoreCars}
        lazyLoading
        listFilters={variables}
      />
    </Stack>
  );
};

export default Cars;
